import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Container, Grid, Paper, Typography, TextField, Button, Box, Avatar, Alert, Divider, CircularProgress 
} from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import RecipeCard from "../components/RecipeCard"; 

// FIREBASE IMPORTS
import { getAuth, onAuthStateChanged, updatePassword, updateEmail, updateProfile } from "firebase/auth";
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebase"; 

const Profile = () => {
  const navigate = useNavigate();
  const auth = getAuth();

  // STATE
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({ 
    username: "", email: "", first_name: "", last_name: "", photoUrl: "" 
  });
  const [recipes, setRecipes] = useState([]);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [isPasswordUser, setIsPasswordUser] = useState(false);
  
  // EDIT FORM STATE
  const [editing, setEditing] = useState(false);
  const [updatedUser, setUpdatedUser] = useState({ 
    username: "", first_name: "", last_name: "", email: "", password: "" 
  });

  // --- 1. FETCH DATA ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      const providerIsPassword = currentUser.providerData.some(
          (provider) => provider.providerId === 'password'
      );
      setIsPasswordUser(providerIsPassword);

      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        let fetchedData = userSnap.exists() ? userSnap.data() : {};

        let fName = fetchedData.first_name || fetchedData.firstName || "";
        let lName = fetchedData.last_name || fetchedData.lastName || "";

        if (!fName && !lName && currentUser.displayName) {
            const nameParts = currentUser.displayName.split(" ");
            fName = nameParts[0] || "";
            lName = nameParts.slice(1).join(" ") || "";
        }

        const rawPhoto = fetchedData.photoUrl || fetchedData.photo_url || currentUser.photoURL || "";

        let displayUName = fetchedData.username;
        if (!displayUName || displayUName.includes("@")) {
          if (displayUName && displayUName.includes("@")) {
            displayUName = displayUName.split("@")[0];
          } else if (currentUser.displayName && !currentUser.displayName.includes("@")) {
            displayUName = currentUser.displayName;
          } else if (fName) {
            displayUName = fName.toLowerCase();
          } else {
            displayUName = currentUser.email ? currentUser.email.split("@")[0] : "Chef";
          }
        }

        const consolidatedUser = {
            username: displayUName,
            email: currentUser.email || "",
            first_name: fName,
            last_name: lName,
            photoUrl: rawPhoto
        };

        setUser(consolidatedUser);
        setUpdatedUser({ 
            username: consolidatedUser.username, 
            first_name: fName, 
            last_name: lName,
            email: consolidatedUser.email,
            password: "" 
        });

        const q = query(collection(db, "recipes"), where("user_id", "==", currentUser.uid));
        const querySnapshot = await getDocs(q);
        setRecipes(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        setError("Failed to load profile data.");
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  const handleChange = (e) => {
    setUpdatedUser({ ...updatedUser, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg("");
    const currentUser = auth.currentUser;

    if (!currentUser) return;
    if (isPasswordUser && updatedUser.password && updatedUser.password.length < 6) {
        return setError("Password must be at least 6 characters.");
    }

    try {
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
          first_name: updatedUser.first_name,
          last_name: updatedUser.last_name,
          username: updatedUser.username,
          ...(isPasswordUser && { email: updatedUser.email })
      });

      if (updatedUser.username) {
        try {
          await updateProfile(currentUser, { displayName: updatedUser.username });
        } catch (pErr) {
          console.warn("Could not sync auth displayName:", pErr);
        }
      }

      if (isPasswordUser) {
          if (updatedUser.email !== currentUser.email) await updateEmail(currentUser, updatedUser.email);
          if (updatedUser.password) await updatePassword(currentUser, updatedUser.password);
      }

      setUser({ ...user, ...updatedUser });
      setSuccessMsg("Profile updated successfully!");
      setEditing(false);

      if (isPasswordUser && updatedUser.password) {
          alert("Password changed. Please log in again.");
          await auth.signOut();
          navigate("/login");
      }
    } catch (err) {
      setError("Failed to update: " + err.message);
    }
  };

  const handleAvatarUpload = async (e) => {
    // 1. Ensure we actually have a file
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    
    console.log("Starting upload for:", file.name);
    setLoading(true);
    
    try {
        const storage = getStorage();
        // Create a reference to the path
        const storageRef = ref(storage, `profile_pics/${auth.currentUser.uid}`);
        
        // 2. Upload the raw file object, not the event or the list
        const snapshot = await uploadBytes(storageRef, file);
        console.log("Upload snapshot:", snapshot);
        
        // 3. Get the URL from the snapshot reference
        const photoUrl = await getDownloadURL(snapshot.ref);
        console.log("Retrieved URL:", photoUrl);
        
        // 4. Save to Firestore
        await updateDoc(doc(db, "users", auth.currentUser.uid), { photoUrl });
        
        // 5. Update state with cache-buster (use & if query parameters already exist)
        const separator = photoUrl.includes('?') ? '&' : '?';
        const timestampedUrl = `${photoUrl}${separator}t=${Date.now()}`;
        setUser(prev => ({ ...prev, photoUrl: timestampedUrl }));
        setSuccessMsg("Profile picture updated!");
    } catch (err) {
        console.error("Upload error details:", err);
        setError("Failed to upload image: " + err.message);
    } finally {
        setLoading(false);
    }
  };

  const getInitials = () => {
    const f = user.first_name?.trim() ? user.first_name.trim()[0] : "";
    const l = user.last_name?.trim() ? user.last_name.trim()[0] : "";
    const initials = (f + l).toUpperCase();
    if (initials) return initials;
    if (user.username?.trim()) return user.username.trim()[0].toUpperCase();
    return <PersonIcon />;
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="60vh"><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
            <Box display="flex" justifyContent="center" mb={2} sx={{ position: 'relative', width: 'fit-content', mx: 'auto' }}>
              <Avatar 
                src={user.photoUrl} 
                alt={user.username || "Chef"}
                sx={{ width: 100, height: 100, bgcolor: 'primary.main', fontSize: '2rem', border: '2px solid #e0e0e0' }}
              >
                {getInitials()}
              </Avatar>
              
              {editing && (
                <Box sx={{ position: 'absolute', bottom: 0, right: 0, zIndex: 10 }}>
                  <input type="file" id="avatar-upload" hidden accept="image/*" onChange={handleAvatarUpload} />
                  <label htmlFor="avatar-upload">
                    <Button component="span" variant="contained" size="small" sx={{ borderRadius: '50%', minWidth: '32px', width: '32px', height: '32px', p: 0 }}>
                      Edit
                    </Button>
                  </label>
                </Box>
              )}
            </Box>
            
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

            {!editing ? (
              <>
                <Typography variant="h5">
                  {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
                </Typography>
                <Typography variant="body1" color="textSecondary" gutterBottom>{user.email}</Typography>
                <Divider sx={{ my: 2 }} />
                <Button variant="contained" fullWidth onClick={() => setEditing(true)}>Edit Profile</Button>
              </>
            ) : (
              <form onSubmit={handleUpdate}>
                <Grid container spacing={2}>
                  <Grid item xs={6}><TextField label="First Name" name="first_name" value={updatedUser.first_name} onChange={handleChange} size="small" fullWidth /></Grid>
                  <Grid item xs={6}><TextField label="Last Name" name="last_name" value={updatedUser.last_name} onChange={handleChange} size="small" fullWidth /></Grid>
                  <Grid item xs={12}><TextField label="Username" name="username" value={updatedUser.username} onChange={handleChange} fullWidth size="small" required /></Grid>
                  
                  {isPasswordUser ? (
                    <>
                      <Grid item xs={12}><TextField label="Email" name="email" value={updatedUser.email} onChange={handleChange} fullWidth size="small" required /></Grid>
                      <Grid item xs={12}><TextField label="New Password" name="password" type="password" placeholder="Leave blank to keep current" onChange={handleChange} fullWidth size="small" helperText="Min 6 characters" /></Grid>
                    </>
                  ) : (
                    <Grid item xs={12}><TextField label="Email" value={user.email} fullWidth size="small" disabled helperText="Managed via Google account" /></Grid>
                  )}
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" fullWidth>Save</Button>
                  <Button variant="outlined" fullWidth onClick={() => setEditing(false)}>Cancel</Button>
                </Box>
              </form>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Typography variant="h4" gutterBottom sx={{ fontFamily: "'Patrick Hand', cursive", color: '#477491' }}>My Recipes</Typography>
          <Divider sx={{ mb: 3 }} />
          {recipes.length === 0 ? <Typography color="textSecondary">You haven't posted any recipes yet.</Typography> : (
            <Grid container spacing={2.5}>
              {recipes.map(r => (
                <Grid item xs={12} sm={6} key={r.id} sx={{ display: 'flex' }}>
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <RecipeCard recipe={r} />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>

      </Grid>
    </Container>
  );
};

export default Profile;